import { useCallback, useEffect, useState } from 'react';
import { autoUpdate } from '@floating-ui/react-dom';
import type {
  Direction,
  ArrowLocation,
  Position,
  OutOfBounds,
  TooltipProps,
} from './tooltip.types';
import { SPACING, ARROW_INSET_PX } from './tooltip.types';

/**
 * Default positioning hook (self-implemented)
 */
export function usePositioning(
  show: boolean,
  initialDirection: Direction,
  arrowLocation: ArrowLocation,
  targetRef?: React.RefObject<HTMLElement | null>,
  tooltipRef?: React.RefObject<HTMLDivElement>,
  customPosition?: TooltipProps['customPosition'],
  children?: React.ReactNode,
) {
  const [direction, setDirection] = useState<Direction>(initialDirection);
  const [position, setPosition] = useState<Position | null>(null);
  const [tooltipWidth, setTooltipWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // ========== Positioning utility functions ==========

  const isOutOfBounds = useCallback(
    (
      pos: Position,
      tooltipRect: DOMRect,
      screenWidth: number,
      screenHeight: number,
    ): OutOfBounds => ({
      left: pos.left < SPACING.screenPadding,
      right: pos.left + tooltipRect.width > screenWidth - SPACING.screenPadding,
      top: pos.top < SPACING.screenPadding,
      bottom:
        pos.top + tooltipRect.height > screenHeight - SPACING.screenPadding,
    }),
    [],
  );

  const resolveArrowLoc = useCallback(
    (loc: ArrowLocation): ArrowLocation => (loc === 'default' ? 'middle' : loc),
    [],
  );

  const getTooltipAnchor = useCallback(
    (dir: Direction, loc: ArrowLocation, tip: DOMRect) => {
      const l = resolveArrowLoc(loc);
      if (dir === 'top' || dir === 'bottom') {
        if (l === 'left') return ARROW_INSET_PX;
        if (l === 'right') return tip.width - ARROW_INSET_PX;
        return tip.width / 2;
      } else {
        if (l === 'top') return ARROW_INSET_PX;
        if (l === 'bottom') return tip.height - ARROW_INSET_PX;
        return tip.height / 2;
      }
    },
    [resolveArrowLoc],
  );

  const computeAlignedPosition = useCallback(
    (
      dir: Direction,
      loc: ArrowLocation,
      target: DOMRect,
      tip: DOMRect,
      dialog?: DOMRect,
    ): Position => {
      const dx = dialog?.left || 0;
      const dy = dialog?.top || 0;

      if (dir === 'top' || dir === 'bottom') {
        const targetX = target.left + target.width / 2;
        const arrowX = getTooltipAnchor(dir, loc, tip);
        return {
          top:
            (dir === 'bottom'
              ? target.bottom + SPACING.gap
              : target.top - tip.height - SPACING.gap) - dy,
          left: targetX - arrowX - dx,
        };
      } else {
        const targetY = target.top + target.height / 2;
        const arrowY = getTooltipAnchor(dir, loc, tip);
        return {
          top: targetY - arrowY - dy,
          left:
            (dir === 'right'
              ? target.right + SPACING.gap
              : target.left - tip.width - SPACING.gap) - dx,
        };
      }
    },
    [getTooltipAnchor],
  );

  // ========== Position update logic ==========

  const updatePosition = useCallback(() => {
    if (!targetRef?.current || !tooltipRef?.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const openDialogPanel = document.querySelector(
      '[data-headlessui-state="open"][id^="headlessui-dialog-panel"]',
    );
    const dialogRect = openDialogPanel?.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const tooltipOverflow =
      tooltipRect.width > screenWidth - 2 * SPACING.screenPadding;

    setTooltipWidth(tooltipRect.width);

    let newDirection = initialDirection;
    let newPosition = computeAlignedPosition(
      newDirection,
      arrowLocation,
      targetRect,
      tooltipRect,
      dialogRect as DOMRect | undefined,
    );

    if (tooltipOverflow) {
      if (!['top', 'bottom'].includes(newDirection)) newDirection = 'top';
      const p = computeAlignedPosition(
        newDirection,
        arrowLocation,
        targetRect,
        tooltipRect,
        dialogRect as DOMRect | undefined,
      );
      newPosition = {
        top: Math.min(
          screenHeight - tooltipRect.height - SPACING.screenPadding,
          Math.max(SPACING.screenPadding, p.top),
        ),
        left: Math.max(
          SPACING.screenPadding,
          (screenWidth - tooltipRect.width) / 2,
        ),
      };
    } else {
      // Horizontal direction adjustment
      let bounds = isOutOfBounds(
        newPosition,
        tooltipRect,
        screenWidth,
        screenHeight,
      );

      if (bounds.right || bounds.left) {
        if (bounds.right && ['right', 'bottom', 'top'].includes(newDirection)) {
          newDirection = 'left';
          newPosition = computeAlignedPosition(
            'left',
            arrowLocation,
            targetRect,
            tooltipRect,
            dialogRect as DOMRect | undefined,
          );
        } else if (
          bounds.left &&
          ['left', 'bottom', 'top'].includes(newDirection)
        ) {
          newDirection = 'right';
          newPosition = computeAlignedPosition(
            'right',
            arrowLocation,
            targetRect,
            tooltipRect,
            dialogRect as DOMRect | undefined,
          );
        } else {
          newPosition.left = bounds.right
            ? screenWidth - tooltipRect.width - SPACING.screenPadding
            : SPACING.screenPadding;
        }
      }

      // Vertical direction adjustment
      bounds = isOutOfBounds(
        newPosition,
        tooltipRect,
        screenWidth,
        screenHeight,
      );
      if (bounds.top) {
        newDirection = 'bottom';
        newPosition = computeAlignedPosition(
          'bottom',
          arrowLocation,
          targetRect,
          tooltipRect,
          dialogRect as DOMRect | undefined,
        );
      } else if (bounds.bottom) {
        newDirection = 'top';
        newPosition = computeAlignedPosition(
          'top',
          arrowLocation,
          targetRect,
          tooltipRect,
          dialogRect as DOMRect | undefined,
        );
      }

      // Final clipping
      newPosition.left = Math.min(
        screenWidth - tooltipRect.width - SPACING.screenPadding,
        Math.max(SPACING.screenPadding, newPosition.left),
      );
      newPosition.top = Math.min(
        screenHeight - tooltipRect.height - SPACING.screenPadding,
        Math.max(SPACING.screenPadding, newPosition.top),
      );
    }

    setDirection(newDirection);
    setPosition((prev) =>
      prev && prev.top === newPosition.top && prev.left === newPosition.left
        ? prev
        : newPosition,
    );
  }, [
    targetRef,
    tooltipRef,
    initialDirection,
    arrowLocation,
    computeAlignedPosition,
    isOutOfBounds,
  ]);

  // ========== Event handling ==========

  // Hover event
  useEffect(() => {
    const element = targetRef?.current;
    if (!element) return;

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [targetRef]);

  useEffect(() => {
    if (!show && !isHovered) return;

    const target = targetRef?.current;
    const tip = tooltipRef?.current;
    if (!target || !tip) return;

    return autoUpdate(target, tip, updatePosition, {
      animationFrame: isHovered,
    });
  }, [show, isHovered, updatePosition, targetRef, tooltipRef]);

  // customPosition centering helper
  useEffect(() => {
    if (customPosition && tooltipRef?.current) {
      const height = tooltipRef.current.getBoundingClientRect().height;
      if (height > 0 && typeof customPosition.top === 'number') {
        tooltipRef.current.style.top = `${-height / 2 + customPosition.top}px`;
      }
    }
  }, [customPosition, children, show, isHovered, tooltipRef]);

  return {
    direction,
    position,
    tooltipWidth,
    isHovered,
  };
}
